import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

export interface AppTheme {
  key: string;
  label: string;
  color: string;       // accent color shown in swatch
  shade: string;
  tint: string;
}

export const THEMES: AppTheme[] = [
  { key: 'default',  label: 'Default',  color: '#E0F64B', shade: '#c5da30', tint: '#e3f75e' },
  { key: 'volt',     label: 'Volt',     color: '#E0F64B', shade: '#c5da30', tint: '#e7f873' },
  { key: 'aqua',     label: 'Aqua',     color: '#22d3ee', shade: '#06a8c3', tint: '#67e8f9' },
  { key: 'royal',    label: 'Royal',    color: '#3b82f6', shade: '#2563eb', tint: '#60a5fa' },
  { key: 'violet',   label: 'Violet',   color: '#8b5cf6', shade: '#7c3aed', tint: '#a78bfa' },
  { key: 'magenta',  label: 'Magenta',  color: '#d946ef', shade: '#c026d3', tint: '#e879f9' },
  { key: 'rose',     label: 'Rose',     color: '#f43f5e', shade: '#e11d48', tint: '#fb7185' },
  { key: 'scarlet',  label: 'Scarlet',  color: '#ef4444', shade: '#dc2626', tint: '#f87171' },
  { key: 'tangerine', label: 'Tangerine', color: '#f97316', shade: '#ea580c', tint: '#fb923c' },
  { key: 'amber',    label: 'Amber',     color: '#f59e0b', shade: '#d97706', tint: '#fbbf24' },
  { key: 'emerald',  label: 'Emerald',  color: '#10b981', shade: '#059669', tint: '#34d399' },
  { key: 'pine',     label: 'Pine',     color: '#15803d', shade: '#166534', tint: '#22c55e' },
  { key: 'teal',     label: 'Teal',     color: '#14b8a6', shade: '#0f9488', tint: '#2dd4bf' },
  { key: 'midnight', label: 'Midnight', color: '#4f46e5', shade: '#4338ca', tint: '#818cf8' },
  { key: 'graphite', label: 'Graphite', color: '#94a3b8', shade: '#64748b', tint: '#cbd5e1' },
  { key: 'copper',   label: 'Copper',   color: '#c47a3a', shade: '#a1622f', tint: '#d69a62' },
  { key: 'orchid',   label: 'Orchid',   color: '#c084fc', shade: '#a855f7', tint: '#d8b4fe' },
  { key: 'ice',      label: 'Ice',      color: '#bae6fd', shade: '#7dd3fc', tint: '#e0f2fe' },
  { key: 'liquid',   label: 'Liquid Glass',   color: '#c084fc', shade: '#a855f7', tint: '#e879f9' },
  { key: 'obsidian', label: 'Obsidian Glass', color: '#94a3b8', shade: '#64748b', tint: '#cbd5e1' },
  { key: 'aurora',   label: 'Aurora',    color: '#22d3ee', shade: '#06b6d4', tint: '#67e8f9' },
  { key: 'ember',    label: 'Ember',     color: '#f97316', shade: '#ea580c', tint: '#fb923c' },
  { key: 'neotokyo', label: 'Neo Tokyo', color: '#ec4899', shade: '#db2777', tint: '#f472b6' },
];

const PREF_KEY = 'appTheme';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  currentTheme: AppTheme = THEMES[0];

  constructor() {
    this.apply(THEMES[0]);
  }

  get isDefaultTheme(): boolean {
    return this.currentTheme.key === 'default';
  }

  async loadSaved() {
    const { value } = await Preferences.get({ key: PREF_KEY });
    const found = THEMES.find(t => t.key === value);
    this.apply(found ?? THEMES[0]);
  }

  async setTheme(theme: AppTheme) {
    this.apply(theme);
    await Preferences.set({ key: PREF_KEY, value: theme.key });
  }

  private apply(theme: AppTheme) {
    this.currentTheme = theme;
    const root = document.documentElement;
    root.classList.toggle('theme-default',   theme.key === 'default');
    root.classList.toggle('theme-liquid',    theme.key === 'liquid');
    root.classList.toggle('theme-obsidian',  theme.key === 'obsidian');
    root.classList.toggle('theme-aurora',    theme.key === 'aurora');
    root.classList.toggle('theme-ember',     theme.key === 'ember');
    root.classList.toggle('theme-neotokyo', theme.key === 'neotokyo');
    root.style.setProperty('--ion-color-secondary',       theme.color);
    root.style.setProperty('--ion-color-secondary-rgb',   this.hexToRgb(theme.color));
    root.style.setProperty('--ion-color-secondary-shade', theme.shade);
    root.style.setProperty('--ion-color-secondary-tint',  theme.tint);
    // Glass themes avec fond sombre → texte blanc sur boutons
    // Magma exception : son gradient est orange clair → texte sombre lisible
    const whiteContrast = ['liquid', 'obsidian', 'aurora', 'neotokyo'].includes(theme.key);
    const contrast    = whiteContrast ? '#ffffff' : '#222428';
    const contrastRgb = whiteContrast ? '255,255,255' : '34,36,40';
    root.style.setProperty('--ion-color-secondary-contrast',     contrast);
    root.style.setProperty('--ion-color-secondary-contrast-rgb', contrastRgb);
  }

  private hexToRgb(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
  }
}
